import { FC, useState } from 'react'
import cn from 'classnames'
import ReactModal from 'react-modal'

import AddCommentForm from './AddCommentForm'
import { useGetDraftReviewsQuery } from '../../useDraftsQuery'
import { getDateTimeString } from 'lib/helpers/dateHelper'
import { Loading } from 'components/Loading'
import ReviewsModal from '../../ReviewsModal'

import styles from './ConfirmationModal.module.scss'

import { ReactComponent as IconClose } from 'images/icons/close.svg'
import { Draft } from '../../types/types'

interface AddCommentModalProps {
  draft: Draft | null
  isOpen: boolean
  onClose: () => void
}

const AddCommentModal: FC<AddCommentModalProps> = ({ draft, isOpen, onClose }) => {
  const [showAllReviews, setShowAllReviews] = useState(false)
  const { data: draftWithReviews, isLoading } = useGetDraftReviewsQuery(draft?.id || 0)

  if (!draft) {
    return null
  }

  const currentDraft = draftWithReviews || draft
  const reviews = currentDraft.recentReviews || []

  return (
    <ReactModal
      isOpen={isOpen}
      className={cn(styles.modal)}
      shouldFocusAfterRender={true}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <IconClose className={styles.closeIcon} onClick={onClose} />
          <div className={styles.sectionName}>Add Comment</div>
          <div className={styles.title}>{draft.data.title}</div>
          <div className={styles.info}>
            Status: <strong>{draft.status}</strong>
          </div>
        </div>

        {/* Show existing comments or no comments message */}
        <div className={styles.commentsSection}>
          <h4 className={styles.commentsTitle}>Previous Comments</h4>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <Loading />
            </div>
          ) : reviews.length > 0 ? (
            <div className={styles.commentsList}>
              {reviews.slice(0, 3).map((review) => (
                <div key={review.id} className={styles.commentItem}>
                  <div className={styles.commentHeader}>
                    <span className={styles.reviewerName}>{review.reviewedBy}</span>
                    <span className={styles.commentDate}>{getDateTimeString(new Date(review.reviewedAt))}</span>
                  </div>
                  <p className={styles.commentText}>{review.comment}</p>
                  <div className={styles.commentStatus}>
                    <span className={`${styles.statusBadge} ${styles[review.status]}`}>
                      {review.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
              {reviews.length > 3 && (
                <div
                  className={styles.moreComments}
                  onClick={() => setShowAllReviews(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setShowAllReviews(true)
                    }
                  }}
                >
                  ... and {reviews.length - 3} more comment{reviews.length - 3 !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.noComments}>
              <p>No recent comments available.</p>
            </div>
          )}
        </div>

        <AddCommentForm draftId={draft.id} handleClose={onClose} />
      </div>

      {showAllReviews && <ReviewsModal draftId={draft.id} onClose={() => setShowAllReviews(false)} />}
    </ReactModal>
  )
}

export default AddCommentModal
